import type { QuotationMetatadaTableDto } from '@api/machines'
import {
  filterQuotesByAction,
  formatDateAndTime,
  getEnvDateFormat,
  getUserProfileFromLocalStorage,
  type QuoteAction,
  QuoteActions
} from '@common/utils'
import {
  type ColumnCellTemplateData,
  MolsonButton,
  MolsonDialog,
  MolsonDialogActions,
  MolsonDialogContent,
  MolsonDialogHeader
} from '@components/reusable'
import { useTranslation } from 'react-i18next'
import {
  useArchiveQuote,
  useDeleteQuote,
  useRestoreQuoteToDraft
} from '@/shared-queries/quotation'
import { ColorizedHeaderDxGrid } from '../custom-dx-grids/CustomDxGrids'
import {
  renderItemNameColumn,
  renderPlainTextColumn
} from '../render-grid-columns'

type ArchiveRestoreItemsDialogProps = {
  onClose: () => void
  open: boolean
  action: QuoteAction
  selectedQuotes: QuotationMetatadaTableDto[]
  redirectToQuote?: (quoteId: string) => void
  redirectToQuoteFromCustomers?: (
    quoteId: string,
    createdBy: string,
    currentUser: string
  ) => void
}

export const QuoteActionsDialog = ({
  onClose,
  open,
  action,
  selectedQuotes,
  redirectToQuote,
  redirectToQuoteFromCustomers
}: ArchiveRestoreItemsDialogProps) => {
  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })

  const user = getUserProfileFromLocalStorage()

  const filteredQuotes = filterQuotesByAction(selectedQuotes, action)

  const filteredQuotesIds = filteredQuotes.map(quote => quote.id) as string[]

  const singleItemSelected = filteredQuotes.length === 1

  const isArchiveAction = action === QuoteActions.ARCHIVE
  const isRestoreAction = action === QuoteActions.RESTORE
  const isDeleteAction = action === QuoteActions.DELETE

  const formattedQuotes = filteredQuotes.map(quote => ({
    id: quote.id ?? '',
    quotationName: quote.quotationName ?? ''
  }))

  const { mutateAsync: archiveQuotes, isPending: isArchiveQuotesPending } =
    useArchiveQuote({
      selectedQuotes: formattedQuotes
    })

  const { mutateAsync: restoreQuotes, isPending: isRestoreQuotesPending } =
    useRestoreQuoteToDraft({
      selectedQuotes: formattedQuotes
    })

  const { mutateAsync: deleteQuotes, isPending: isDeleteQuotesPending } =
    useDeleteQuote({
      selectedQuotes: formattedQuotes
    })

  const isActionPending =
    isArchiveQuotesPending || isRestoreQuotesPending || isDeleteQuotesPending

  const getDialogText = () => {
    switch (action) {
      case QuoteActions.ARCHIVE:
        return {
          title: singleItemSelected
            ? t('youWantToArchiveQuote')
            : t('youWantToArchiveQuotes', {
                nrOfItems: filteredQuotes.length.toString()
              }),
          subtitle: singleItemSelected
            ? t('archiveItemSubtitle')
            : t('archiveItemsSubtitle')
        }
      case QuoteActions.DELETE:
        return {
          title: singleItemSelected
            ? t('youWantToDeleteQuote')
            : t('youWantToDeleteQuotes', {
                nrOfItems: filteredQuotes.length.toString()
              }),
          subtitle: singleItemSelected
            ? t('quoteWillBeDeleted')
            : t('quotesWillBeDeleted')
        }
      case QuoteActions.RESTORE:
        return {
          title: singleItemSelected
            ? t('restoreQuoteToDraftTitle')
            : t('youWantToRestoreQuotes', {
                nrOfItems: filteredQuotes.length.toString()
              }),
          subtitle: singleItemSelected
            ? t('restoringQuoteToDraft')
            : t('restoringQuotesToDraft')
        }
      default:
        return {
          title: '',
          subtitle: ''
        }
    }
  }

  const getActionButtonText = () => {
    switch (action) {
      case QuoteActions.ARCHIVE:
        return singleItemSelected ? 'archiveQuote' : 'archiveQuotes'
      case QuoteActions.DELETE:
        return singleItemSelected ? 'deleteQuote' : 'deleteQuotes'
      case QuoteActions.RESTORE:
        return singleItemSelected ? 'restoreQuote' : 'restoreQuotes'
    }
  }

  const closeDialogAndNavigate = (params: ColumnCellTemplateData) => {
    onClose()

    redirectToQuote?.(params.data.id) ??
      redirectToQuoteFromCustomers?.(
        params.data.id,
        params.data.createdBy,
        user?.preferred_username
      )
  }

  const handleAction = async (quotesIds: string[]) => {
    if (isArchiveAction) {
      await archiveQuotes({ quotesIds })
    } else if (isRestoreAction) {
      await restoreQuotes({ quotesIds })
    } else {
      await deleteQuotes({ quotesIds })
    }

    onClose()
  }

  return (
    <MolsonDialog fullWidth={true} maxWidth="md" onClose={onClose} open={open}>
      <MolsonDialogHeader
        displayDivider={false}
        headerIcon="WARNING"
        iconColor={isDeleteAction ? 'error' : 'warning'}
        subTitle={getDialogText().subtitle}
        title={getDialogText()?.title}
      />
      <MolsonDialogContent sx={{ pb: 0 }}>
        <ColorizedHeaderDxGrid
          pagingProps={{
            pageSize: 5
          }}
          filterRow={{
            visible: false
          }}
          dataSource={filteredQuotes}
          columnsProps={[
            {
              dataField: 'quotationName',
              caption: `${t('quoteName')}`,
              cellRender: params =>
                renderItemNameColumn({
                  params,
                  handleOnClick: () => closeDialogAndNavigate(params),
                  root: 'quoteActionsDialog'
                })
            },
            {
              dataField: 'customerName',
              caption: `${t('customerName')}`
            },
            {
              dataField: 'createDateTime',
              caption: `${t('dateCreated')}`,
              dataType: 'date',
              width: 180,
              editorOptions: {
                max: new Date()
              },
              format: getEnvDateFormat(true),
              cellRender: ({ value }: { value: string }) => {
                return formatDateAndTime(value, {
                  date: true,
                  time: false
                })
              }
            },
            {
              dataField: 'price',
              caption: `${t('dealValue')}`,
              width: 180,
              alignment: 'right',
              cellRender: params =>
                renderPlainTextColumn({ params, key: 'price' }),
              headerFilter: {
                groupInterval: 10000
              }
            }
          ]}
        />
      </MolsonDialogContent>
      <MolsonDialogActions displayDivider={false}>
        <MolsonButton color="secondary" onClick={onClose} variant="outlined">
          {t('cancel')}
        </MolsonButton>

        <MolsonButton
          onClick={() => handleAction(filteredQuotesIds)}
          variant="contained"
          color={isDeleteAction ? 'error' : 'primary'}
          loading={isActionPending}
        >
          {t(`${getActionButtonText()}`)}
        </MolsonButton>
      </MolsonDialogActions>
    </MolsonDialog>
  )
}
